'use client';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { ListingTypeService } from '@/hotdesk-ui/services/listing-type.service';
import { RootState } from '../store';

export interface ListingTypeItem {
    id: number;
    type: string;
    icon: string; // URL — from API or fallback to local SVG
}

interface ListingTypesState {
    items: ListingTypeItem[];
    loading: boolean;
    error: string | null;
}

const initialState: ListingTypesState = {
    items: [],
    loading: false,
    error: null
};

export const fetchListingTypes = createAsyncThunk(
    'listingTypes/fetchListingTypes',
    async (locale: string | undefined, { getState }) => {
        // Prefer the explicit locale passed by the caller; fall back to the
        // listing-slice default. Without this, items end up cached in English
        // because state.listing.defaultLanguage is initialised to 'en' and
        // never updated when the user is on a non-English locale.
        const state = getState() as RootState;
        const lang = locale || state.listing.defaultLanguage;
        const apiConfig = await new ApiConfigration().getApiConfig(lang);
        const service = new ListingTypeService(apiConfig);
        const res = await service.getListingType(lang);
        const results = (res as any)?.results || [];
        return results.map((t: any) => ({
            id: t.id,
            type: t.type,
            icon: t.url || `/assets/icons/icon+${t.id}.svg`
        }));
    }
);

const listingTypesSlice = createSlice({
    name: 'listingTypes',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchListingTypes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchListingTypes.fulfilled, (state, action) => {
                state.items = action.payload;
                state.loading = false;
            })
            .addCase(fetchListingTypes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch listing types';
            });
    }
});

export default listingTypesSlice.reducer;
